import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import type { ScheduleResponse, ScheduleBlock } from '../lib/api'

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const HOUR_HEIGHT = 20
const DISPLAY_HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22]

const SCHEDULE_COLORS = [
  { bg: 'bg-brand/30', border: 'border-brand/40', text: 'text-gray-800' },
  { bg: 'bg-rose-400', border: 'border-rose-500', text: 'text-white' },
  { bg: 'bg-blue-400', border: 'border-blue-500', text: 'text-white' },
  { bg: 'bg-amber-400', border: 'border-amber-500', text: 'text-white' },
  { bg: 'bg-purple-400', border: 'border-purple-500', text: 'text-white' },
  { bg: 'bg-cyan-400', border: 'border-cyan-500', text: 'text-white' },
]

const END_OF_DAY_TIME = '23:59'

interface SelectionState {
  dayOfWeek: number
  startHour: number
  endHour: number
}

interface FocusedCell {
  dayIndex: number
  hour: number
}

interface WeeklyScheduleProps {
  schedules: ScheduleResponse[]
  timezone: string
  onCreateBlock: (dayOfWeek: number, startTime: string, endTime: string) => void
  onClickBlock: (schedule: ScheduleResponse, block: ScheduleBlock) => void
}

function hourToTime(hour: number): string {
  if (hour >= 24) return END_OF_DAY_TIME
  return `${hour.toString().padStart(2, '0')}:00`
}

function timeToDecimalHours(time: string): number {
  const parts = time.split(':')
  const hours = parseInt(parts[0], 10) || 0
  const minutes = parseInt(parts[1], 10) || 0
  return hours + minutes / 60
}

function formatHour(hour: number): string {
  if (hour === 0) return '12a'
  if (hour === 12) return '12p'
  if (hour < 12) return `${hour}a`
  return `${hour - 12}p`
}

function getCurrentTimeInTimezone(date: Date, timezone: string): { hour: number; minute: number; dayOfWeek: number } {
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  })

  const dayFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
  })

  const timeParts = timeFormatter.formatToParts(date)
  const dayStr = dayFormatter.format(date)

  const hourPart = timeParts.find(p => p.type === 'hour')
  const minutePart = timeParts.find(p => p.type === 'minute')

  const hour = hourPart ? parseInt(hourPart.value, 10) : 0
  const minute = minutePart ? parseInt(minutePart.value, 10) : 0

  const weekdayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
  const dayOfWeek = weekdayMap[dayStr] ?? 0

  return { hour, minute, dayOfWeek }
}

export default function WeeklySchedule({
  schedules,
  timezone,
  onCreateBlock,
  onClickBlock,
}: WeeklyScheduleProps) {
  const [selection, setSelection] = useState<SelectionState | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [currentTime, setCurrentTime] = useState<Date>(new Date())
  const [focusedCell, setFocusedCell] = useState<FocusedCell | null>(null)
  const [keyboardSelectionStart, setKeyboardSelectionStart] = useState<FocusedCell | null>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const dayColumnRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  const getScheduleColor = useCallback((scheduleId: string) => {
    const index = schedules.findIndex((s) => s.id === scheduleId)
    return SCHEDULE_COLORS[index % SCHEDULE_COLORS.length]
  }, [schedules])

  const getHourFromPosition = useCallback((clientY: number, gridTop: number): number => {
    const relativeY = clientY - gridTop
    const hour = Math.floor(relativeY / HOUR_HEIGHT)
    return Math.max(0, Math.min(23, hour))
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent, dayOfWeek: number) => {
    if (!gridRef.current) return
    const dayColumn = e.currentTarget.getBoundingClientRect()
    const hour = getHourFromPosition(e.clientY, dayColumn.top)
    setSelection({ dayOfWeek, startHour: hour, endHour: hour + 1 })
    setIsDragging(true)
  }, [getHourFromPosition])

  const handleMouseMove = useCallback((e: React.MouseEvent, dayOfWeek: number) => {
    if (!isDragging || !selection || selection.dayOfWeek !== dayOfWeek) return
    const dayColumn = e.currentTarget.getBoundingClientRect()
    const hour = getHourFromPosition(e.clientY, dayColumn.top)
    setSelection((prev) => {
      if (!prev) return null
      const newEndHour = hour < prev.startHour ? hour : Math.min(24, hour + 1)
      return { ...prev, endHour: newEndHour }
    })
  }, [isDragging, selection, getHourFromPosition])

  const handleMouseUp = useCallback(() => {
    if (selection && isDragging) {
      const minHour = Math.min(selection.startHour, selection.endHour)
      const maxHour = Math.max(selection.startHour, selection.endHour)
      if (maxHour - minHour >= 1) {
        onCreateBlock(selection.dayOfWeek, hourToTime(minHour), hourToTime(maxHour))
      }
    }
    setSelection(null)
    setIsDragging(false)
  }, [selection, isDragging, onCreateBlock])

  const handleTouchStart = useCallback((e: React.TouchEvent, dayOfWeek: number) => {
    if (!gridRef.current) return
    const touch = e.touches[0]
    const dayColumn = e.currentTarget.getBoundingClientRect()
    const hour = getHourFromPosition(touch.clientY, dayColumn.top)
    setSelection({ dayOfWeek, startHour: hour, endHour: hour + 1 })
    setIsDragging(true)
  }, [getHourFromPosition])

  const handleTouchMove = useCallback((e: React.TouchEvent, dayOfWeek: number) => {
    if (!isDragging || !selection || selection.dayOfWeek !== dayOfWeek) return
    const touch = e.touches[0]
    const dayColumn = e.currentTarget.getBoundingClientRect()
    const hour = getHourFromPosition(touch.clientY, dayColumn.top)
    setSelection((prev) => {
      if (!prev) return null
      const newEndHour = hour < prev.startHour ? hour : Math.min(24, hour + 1)
      return { ...prev, endHour: newEndHour }
    })
  }, [isDragging, selection, getHourFromPosition])

  const handleTouchEnd = useCallback(() => {
    handleMouseUp()
  }, [handleMouseUp])

  const handleKeyDown = useCallback((e: React.KeyboardEvent, dayIndex: number) => {
    const currentHour = focusedCell?.dayIndex === dayIndex ? focusedCell.hour : 0

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        const nextHour = Math.min(23, currentHour + 1)
        setFocusedCell({ dayIndex, hour: nextHour })
        if (keyboardSelectionStart) {
          setSelection({
            dayOfWeek: keyboardSelectionStart.dayIndex,
            startHour: keyboardSelectionStart.hour,
            endHour: nextHour + 1,
          })
        }
        break
      }
      case 'ArrowUp': {
        e.preventDefault()
        const prevHour = Math.max(0, currentHour - 1)
        setFocusedCell({ dayIndex, hour: prevHour })
        if (keyboardSelectionStart) {
          setSelection({
            dayOfWeek: keyboardSelectionStart.dayIndex,
            startHour: prevHour,
            endHour: keyboardSelectionStart.hour + 1,
          })
        }
        break
      }
      case 'ArrowRight': {
        e.preventDefault()
        const nextDay = Math.min(6, dayIndex + 1)
        setFocusedCell({ dayIndex: nextDay, hour: currentHour })
        setKeyboardSelectionStart(null)
        setSelection(null)
        dayColumnRefs.current[nextDay]?.focus()
        break
      }
      case 'ArrowLeft': {
        e.preventDefault()
        const prevDay = Math.max(0, dayIndex - 1)
        setFocusedCell({ dayIndex: prevDay, hour: currentHour })
        setKeyboardSelectionStart(null)
        setSelection(null)
        dayColumnRefs.current[prevDay]?.focus()
        break
      }
      case 'Enter':
      case ' ': {
        e.preventDefault()
        if (!keyboardSelectionStart) {
          // Start selection
          setKeyboardSelectionStart({ dayIndex, hour: currentHour })
          setSelection({ dayOfWeek: dayIndex, startHour: currentHour, endHour: currentHour + 1 })
        } else {
          // Commit selection
          const minHour = Math.min(keyboardSelectionStart.hour, currentHour)
          const maxHour = Math.max(keyboardSelectionStart.hour, currentHour) + 1
          if (maxHour - minHour >= 1) {
            onCreateBlock(keyboardSelectionStart.dayIndex, hourToTime(minHour), hourToTime(maxHour))
          }
          setKeyboardSelectionStart(null)
          setSelection(null)
        }
        break
      }
      case 'Escape': {
        setKeyboardSelectionStart(null)
        setSelection(null)
        break
      }
    }
  }, [focusedCell, keyboardSelectionStart, onCreateBlock])

  const blocksByDay = useMemo(() => {
    const result: Record<number, Array<{ schedule: ScheduleResponse; block: ScheduleBlock }>> = {}
    for (let day = 0; day < 7; day++) {
      result[day] = []
    }
    for (const schedule of schedules) {
      if (!schedule.isEnabled) continue
      for (const block of schedule.blocks) {
        result[block.dayOfWeek].push({ schedule, block })
      }
    }
    return result
  }, [schedules])

  return (
    <div
      className="bg-white rounded-lg overflow-hidden select-none border border-gray-200"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchEnd={handleTouchEnd}
    >
      <div role="grid" className="grid grid-cols-[44px_repeat(7,1fr)] border-b border-gray-200">
        <div className="py-2 text-center text-xs text-gray-400" />
        {DAYS.map((day) => (
          <div key={day} className="py-2 text-center text-xs font-medium text-gray-600 border-l border-gray-100">
            {day}
          </div>
        ))}
      </div>

      <div
        ref={gridRef}
        className="grid grid-cols-[44px_repeat(7,1fr)] relative"
        style={{ height: `${24 * HOUR_HEIGHT}px` }}
      >
        <div className="relative">
          {DISPLAY_HOURS.map((hour) => (
            <div
              key={hour}
              className="absolute right-1 text-[10px] text-gray-400 -translate-y-1/2"
              style={{ top: `${hour * HOUR_HEIGHT}px` }}
            >
              {formatHour(hour)}
            </div>
          ))}
        </div>

        {(() => {
          const { hour, minute, dayOfWeek } = getCurrentTimeInTimezone(currentTime, timezone)
          const currentTimeDecimal = hour + minute / 60

          return DAYS.map((day, dayIndex) => {
            const isToday = dayOfWeek === dayIndex
            const focusedHour = focusedCell?.dayIndex === dayIndex ? focusedCell.hour : null

            return (
              <div
                key={day}
                ref={(el) => { dayColumnRefs.current[dayIndex] = el }}
                role="gridcell"
                tabIndex={0}
                aria-label={`${DAY_NAMES[dayIndex]} schedule column — use arrow keys to navigate, Enter to select a time block`}
                className="relative border-l border-gray-100 cursor-crosshair touch-none focus:outline-2 focus:outline-brand focus:outline-offset-[-2px]"
                onMouseDown={(e) => handleMouseDown(e, dayIndex)}
                onMouseMove={(e) => handleMouseMove(e, dayIndex)}
                onTouchStart={(e) => handleTouchStart(e, dayIndex)}
                onTouchMove={(e) => handleTouchMove(e, dayIndex)}
                onFocus={() => setFocusedCell((prev) => prev?.dayIndex === dayIndex ? prev : { dayIndex, hour: 0 })}
                onBlur={() => {
                  setFocusedCell(null)
                  setKeyboardSelectionStart(null)
                  setSelection(null)
                }}
                onKeyDown={(e) => handleKeyDown(e, dayIndex)}
              >
                {DISPLAY_HOURS.map((hour) => (
                  <div
                    key={hour}
                    className="absolute left-0 right-0 border-t border-gray-100"
                    style={{ top: `${hour * HOUR_HEIGHT}px` }}
                  />
                ))}

                {focusedHour !== null && (
                  <div
                    className="absolute left-0.5 right-0.5 bg-brand/10 border border-brand/40 rounded pointer-events-none z-5"
                    style={{
                      top: `${focusedHour * HOUR_HEIGHT}px`,
                      height: `${HOUR_HEIGHT}px`,
                    }}
                    aria-label={`${DAY_NAMES[dayIndex]} ${formatHour(focusedHour)}`}
                  />
                )}

                {isToday && (
                  <div
                    className="absolute left-0 right-0 z-30 pointer-events-none"
                    style={{ top: `${currentTimeDecimal * HOUR_HEIGHT}px` }}
                  >
                    <div className="relative flex items-center">
                      <div className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                      <div className="flex-1 h-0.5 bg-red-500" />
                    </div>
                  </div>
                )}

                {selection && selection.dayOfWeek === dayIndex && (
                  <div
                    className="absolute left-0.5 right-0.5 bg-brand/20 border border-brand rounded z-10"
                    style={{
                      top: `${Math.min(selection.startHour, selection.endHour) * HOUR_HEIGHT}px`,
                      height: `${Math.abs(selection.endHour - selection.startHour) * HOUR_HEIGHT}px`,
                    }}
                  />
                )}

                {blocksByDay[dayIndex].map(({ schedule, block }) => {
                  const startDecimal = timeToDecimalHours(block.startTime)
                  const endDecimal = timeToDecimalHours(block.endTime)
                  const color = getScheduleColor(schedule.id)
                  const isOvernight = endDecimal <= startDecimal
                  const displayEndDecimal = isOvernight ? 24 : endDecimal
                  const blockHeight = (displayEndDecimal - startDecimal) * HOUR_HEIGHT

                  return (
                    <div
                      key={block.id}
                      role="button"
                      tabIndex={0}
                      aria-label={`${schedule.name}: ${DAY_NAMES[dayIndex]} ${block.startTime}–${block.endTime}`}
                      className={`absolute left-0.5 right-0.5 ${color.bg} ${color.text} rounded border ${color.border} cursor-pointer hover:brightness-110 active:brightness-110 transition-all overflow-hidden z-20 focus:outline-2 focus:outline-white focus:outline-offset-[-2px]`}
                      style={{
                        top: `${startDecimal * HOUR_HEIGHT}px`,
                        height: `${blockHeight}px`,
                        minHeight: '16px',
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        onClickBlock(schedule, block)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          onClickBlock(schedule, block)
                        }
                      }}
                    >
                      {blockHeight >= 16 && (
                        <div className="px-1 py-0.5 text-[10px] font-medium truncate">
                          {schedule.name}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })
        })()}
      </div>

      {schedules.length > 0 && (
        <div className="px-3 py-2 border-t border-gray-100 flex flex-wrap gap-3">
          {schedules.map((schedule) => {
            const color = getScheduleColor(schedule.id)
            return (
              <div key={schedule.id} className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className={`w-2.5 h-2.5 rounded-sm ${color.bg}`} />
                <span className={!schedule.isEnabled ? 'line-through opacity-50' : ''}>
                  {schedule.name}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
